import { useEffect, useState } from "react";
import { isSameKnownBuild } from "../../utils/pwaBuildIdentity";
import { GIT_SHA } from "../../utils/version";

interface BuildMetadata {
  gitSha?: string;
}

const CHECK_INTERVAL_MS = 60_000;
const REFRESH_KEY_PREFIX = "pt:pax-build-refresh:";
const SAFE_AUTO_REFRESH_PATHS = new Set([
  "/",
  "/auth/magic",
]);

export function shouldRefreshPaxBuild(
  currentSha: string,
  latestSha?: string,
): boolean {
  return currentSha !== "unknown"
    && Boolean(latestSha)
    && !isSameKnownBuild(currentSha, latestSha);
}

export function canAutoRefreshPaxPath(pathname: string): boolean {
  return SAFE_AUTO_REFRESH_PATHS.has(pathname);
}

async function fetchLatestBuildSha(): Promise<string | undefined> {
  const response = await fetch("/build-meta.json?t=" + Date.now(), {
    cache: "no-store",
  });
  if (!response.ok) return undefined;
  const metadata = (await response.json()) as BuildMetadata;
  return metadata.gitSha;
}

async function clearPaxApplicationCaches(): Promise<void> {
  const cleanup: Promise<unknown>[] = [];

  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations().catch(() => []);
    cleanup.push(...registrations.map((registration) => registration.unregister()));
  }

  if ("caches" in window) {
    const cacheNames = await caches.keys().catch(() => []);
    cleanup.push(...cacheNames.map((cacheName) => caches.delete(cacheName)));
  }

  await Promise.allSettled(cleanup);
}

async function reloadPaxToLatestBuild(latestSha: string): Promise<void> {
  try {
    sessionStorage.setItem(
      REFRESH_KEY_PREFIX + latestSha.toLowerCase(),
      "attempted",
    );
  } catch {
    // Cache removal still prevents a stale-shell loop when storage is blocked.
  }
  await clearPaxApplicationCaches();
  window.location.reload();
}
/**
 * Pax sessions have no in-flight GDS command state, so they can recover from a
 * silently stale but otherwise valid JavaScript bundle automatically. The staff
 * application deliberately keeps its manual update prompt instead.
 */
export function PaxBuildFreshness() {
  const [latestBuildSha, setLatestBuildSha] = useState<string | null>(null);
  useEffect(() => {
    let disposed = false;
    let refreshInProgress = false;

    const checkForCurrentBuild = async () => {
      if (disposed || refreshInProgress) return;

      const latestSha = await fetchLatestBuildSha().catch(() => undefined);
      if (!shouldRefreshPaxBuild(GIT_SHA, latestSha) || !latestSha) return;

      if (!canAutoRefreshPaxPath(window.location.pathname)) {
        setLatestBuildSha(latestSha);
        return;
      }

      const refreshKey = REFRESH_KEY_PREFIX + latestSha.toLowerCase();
      try {
        if (sessionStorage.getItem(refreshKey) === "attempted") {
          setLatestBuildSha(latestSha);
          return;
        }
      } catch {
        // Continue with cache recovery when storage is unavailable.
      }

      refreshInProgress = true;
      await reloadPaxToLatestBuild(latestSha);
    };

    void checkForCurrentBuild();
    const interval = window.setInterval(
      () => void checkForCurrentBuild(),
      CHECK_INTERVAL_MS,
    );

    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, []);

  if (!latestBuildSha) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md rounded-xl border border-sky-200 bg-white p-4 text-sm text-slate-700 shadow-xl"
    >
      <div className="font-semibold text-slate-950">Portal update ready</div>
      <p className="mt-1 text-xs text-slate-600">
        Reload when convenient to use the latest client-portal improvements.
      </p>
      <button
        type="button"
        onClick={() => void reloadPaxToLatestBuild(latestBuildSha)}
        className="mt-3 rounded-lg bg-sky-700 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-800 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
      >
        Reload portal
      </button>
    </div>
  );
}
